import type { State } from '../State';
import { isDynamicTestInBucket } from '../getters/ab';
import ab from './ab';
import { isGuestUser } from '../guest/guest.getters';

export default function features<
  T extends Partial<State> & { ruleset: State['ruleset']; id: string },
>(state: T): Features {
  const isGuest = isGuestUser(state);

  const openWebV2 = isDynamicTestInBucket(
    state,
    ab.tests.FEATURE_OPEN_WEB_V2,
    'enabled_web',
  );

  const openWebPayments = openWebV2;

  // Instead of LIFF, allow the opening of the In-App Browser that opens within LINE. No force is applied.
  const alternativeWeb = openWebPayments;

  const teamsTest = isDynamicTestInBucket(
    state,
    ab.tests.TEST_MATCH2_TEAMS,
    'enabled_teams',
  );

  const isDemoMode = isDynamicTestInBucket(
    state,
    ab.tests.TEST_DEMO_MODE,
    'afterFirstPuzzle',
    'afterTutorial',
  );

  const deterministicFate = isDynamicTestInBucket(
    state,
    ab.tests.TEST_DETERMINISTIC_SICK_FATE,
    'enabled',
  );

  const enableADs = isDynamicTestInBucket(
    state,
    ab.tests.FEATURE_ADS,
    'enabled_ads',
  );

  const newFirst2ObstacleVisuals = isDynamicTestInBucket(
    state,
    ab.tests.TEST_NEW_FIRST_2_OBSTACLE_VISUALS,
    'enabled',
  );

  const nativeCrossplayIOS = isDynamicTestInBucket(
    state,
    ab.tests.FEATURE_NATIVE_CROSSPLAY_IOS,
    'enabled',
  );

  const nativeCrossplayAndroid = isDynamicTestInBucket(
    state,
    ab.tests.FEATURE_NATIVE_CROSSPLAY_ANDROID,
    'enabled',
  );

  const addFriendsV2 = !isGuest;
  // Suppresses selected Amplitude analytics events when enabled.
  const amplitudeEventThrottle = isDynamicTestInBucket(
    state,
    ab.tests.FEATURE_AMPLITUDE_EVENT_THROTTLE,
    'enabled',
  );

  /**
   * Rate-limits pet status LINE OA messages using daily/half-day/per-type caps
   * that ramp up based on days since install (segments A/B/C).
   * Low-priority statuses (very_hungry, very_dirty, etc.) are suppressed entirely.
   * Resolved from A/B tests 0432 and 0441 to always-enabled.
   */
  const statusMessageRamp = true;

  const wantToInviteV2 = !isGuest;

  const flyingHighRedesign = isDynamicTestInBucket(
    state,
    ab.tests.TEST_FLYING_HIGH_REDESIGN,
    'enabled',
  );

  const suppressFeatureIntros = isDynamicTestInBucket(
    state,
    ab.tests.TEST_SUPPRESS_FEATURE_INTROS_V2,
    'enabled',
  );

  return {
    isDemoMode: isGuest && isDemoMode,
    isGuestMode: isGuest,
    addFriendsV2,
    friendList: !isGuest,
    friendReferral: !isGuest,
    gifts: !isGuest,
    ingameProfile: !isGuest,
    visitFriend: !isGuest,
    duo: !isGuest,
    nativeIos: nativeCrossplayIOS,
    nativeAndroid: nativeCrossplayAndroid,
    nativeAndroidCrossplayPromo: nativeCrossplayAndroid,
    alternativeWeb,
    teams: !isGuest && teamsTest,
    teamsTournaments: !isGuest && teamsTest,
    openWebPayments,
    deterministicFate,
    match2StarTournament: !isGuest,
    match2FlyingHighTournament: !isGuest,
    flyingHighRedesign,
    weeklyLeaderboards: !isGuest,
    ads: enableADs,
    newFirst2ObstacleVisuals,
    /**
     * Gates the TOS (Terms of Service) nudge on the Daily Bonus collect flow.
     * When enabled, users who haven't accepted LINE Purchase TOS are shown a
     * consent prompt before claiming their daily bonus reward (level >= 50).
     * This drives IAP eligibility by converting daily bonus collectors into
     * TOS-accepted users who can make in-app purchases.
     * Resolved from A/B test 0433 to always-enabled.
     */
    iapTosDailyBonus: true,
    friendListSuggestions: false,
    amplitudeEventThrottle,
    statusMessageRamp,
    bakingBuddies: !isGuest,
    /**
     * Pet invite nudge that encourages users to add friends.
     * Resolved from A/B test 0440 to always-enabled for non-guest users.
     */
    wantToInvite: !isGuest,
    /**
     * Follow-up to 0440. Replaces the 2nd-evolution gate with a 10-minute
     * cumulative awake-time gate (D0-eligible), adds a friend-count-scaled
     * cooldown floor before the probability roll.
     * Resolved to always-enabled for non-guest users.
     */
    wantToInviteV2,
    /**
     * Pet Janken rock-paper-scissors mini-game.
     * Resolved from A/B test 0444 to always-enabled.
     */
    petJanken: !isGuest,
    /**
     * Pet dialog CTA for adding friends.
     * Resolved from A/B test 0478 to always-enabled for non-guest users.
     */
    petDialogAddFriendCtas: !isGuest,
    /**
     * Friend List cluster suggestions.
     * Resolved from A/B test 0481 to always-enabled for non-guest users.
     */
    friendListClusterSuggestions: !isGuest,
    /**
     * Expanded pet dialogs, resolved from A/B 0482 to always-enabled.
     * Retained for shared asset loading and analytics consumers. Follow-up:
     * remove this flag and simplify the former bucket-based asset selection.
     */
    newPetDialogs: true,
    /**
     * Cards Collection V2.
     * Resolved from A/B test 0486 to always-enabled for non-guest users.
     */
    cardsCollectionV2: !isGuest,
    suppressFeatureIntros,
    dreamDrift: true,
  };
}

export type FeatureType =
  | 'isDemoMode'
  | 'isGuestMode'
  | 'addFriendsV2'
  | 'friendList'
  | 'friendReferral'
  | 'gifts'
  | 'ingameProfile'
  | 'visitFriend'
  | 'duo'
  | 'nativeIos'
  | 'nativeAndroid'
  | 'nativeAndroidCrossplayPromo'
  | 'alternativeWeb'
  | 'teams'
  | 'teamsTournaments'
  | 'openWebPayments'
  | 'deterministicFate'
  | 'match2StarTournament'
  | 'match2FlyingHighTournament'
  | 'flyingHighRedesign'
  | 'weeklyLeaderboards'
  | 'ads'
  | 'newFirst2ObstacleVisuals'
  | 'iapTosDailyBonus'
  | 'friendListSuggestions'
  | 'amplitudeEventThrottle'
  | 'statusMessageRamp'
  | 'bakingBuddies'
  | 'wantToInvite'
  | 'wantToInviteV2'
  | 'petJanken'
  | 'petDialogAddFriendCtas'
  | 'friendListClusterSuggestions'
  | 'newPetDialogs'
  | 'cardsCollectionV2'
  | 'suppressFeatureIntros'
  | 'dreamDrift';

export type Features = Record<FeatureType, boolean>;
